import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";

// Follow a brand to get an email nudge (and a Saved > Brand alerts entry) when new pieces arrive.
export default function BrandFollowButton({ brand, className = "", size = 13 }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !brand) { setFollowing(false); return; }
    api.get("/brands/following")
      .then((r) => setFollowing((r.data || []).some((b) => b.brand_key === brand.toLowerCase())))
      .catch(() => {});
  }, [user, brand]);

  if (!brand) return null;

  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav("/auth"); return; }
    setBusy(true);
    try {
      if (following) {
        await api.delete(`/brands/follow/${encodeURIComponent(brand.toLowerCase())}`);
        setFollowing(false);
        toast.success(`Unfollowed ${brand}`);
      } else {
        await api.post("/brands/follow", { brand });
        setFollowing(true);
        toast.success(`Following ${brand} — we'll email you when new pieces arrive`);
      }
    } catch {
      toast.error("Couldn't update follow");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`follow-brand-${brand}`}
      data-following={following}
      className={`inline-flex items-center gap-1.5 chip ${className}`}
      style={following ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}
      title={following ? `Following ${brand}` : `Follow ${brand} for new-arrival alerts`}
    >
      {following ? <BellRing size={size} /> : <Bell size={size} />}
      {following ? "Following" : `Follow ${brand}`}
    </button>
  );
}
